import React, { useState } from 'react';
import { Link } from '@inertiajs/react';
import BlogLayout from '@/components/admin/BlogLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Pencil, ExternalLink, Calendar, User, Tag as TagIcon, FolderOpen, Eye, Code } from 'lucide-react';
import { TipTapContent } from '@/components/editor/TipTapContent';

interface Author {
  id: number;
  name: string;
  email: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
}

interface Post {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  ai_markdown: string;
  tiptap_json: Record<string, unknown>;
  content_html: string;
  status: 'draft' | 'published' | 'archived';
  visibility: 'visible' | 'auth_only';
  published_at: string | null;
  created_at: string;
  updated_at: string;
  author: Author;
  categories: Category[];
  tags: Tag[];
}

interface PostShowProps {
  post: Post;
  canEdit?: boolean;
}

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-yellow-100 text-yellow-800',
};

const statusLabels = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const visibilityLabels = {
  visible: 'Public',
  auth_only: 'Members Only',
};

const PostShow: React.FC<PostShowProps> = ({ post, canEdit = true }) => {
  const [viewMode, setViewMode] = useState<'rendered' | 'markdown'>('rendered');
  
  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Blog', href: '/admin/blog/posts' },
    { label: post.title },
  ];

  return (
    <BlogLayout breadcrumbs={breadcrumbs}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link href="/admin/blog/posts">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{post.title}</h1>
              <p className="text-muted-foreground">View post details</p>
            </div>
          </div>
          <div className="flex gap-2">
            {post.status === 'published' && (
              <Button variant="outline" asChild>
                <Link href={`/blog/${post.slug}`} target="_blank">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View on Blog
                </Link>
              </Button>
            )}
            {canEdit && (
              <Button asChild>
                <Link href={`/admin/blog/posts/${post.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Content</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === 'rendered' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('rendered')}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Rendered
                  </Button>
                  <Button
                    variant={viewMode === 'markdown' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('markdown')}
                  >
                    <Code className="h-4 w-4 mr-1" />
                    Markdown
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {viewMode === 'rendered' ? (
                  <div className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-none text-foreground">
                    {/* Priority: content_html > tiptap_json > content > ai_markdown */}
                    {post.content_html ? (
                      <div dangerouslySetInnerHTML={{ __html: post.content_html }} />
                    ) : post.tiptap_json && Object.keys(post.tiptap_json).length > 0 ? (
                      <TipTapContent 
                        content={post.tiptap_json} 
                        html={post.content}
                      />
                    ) : post.content ? (
                      <div dangerouslySetInnerHTML={{ __html: post.content }} />
                    ) : post.ai_markdown ? (
                      // Fallback: render markdown as plain text for now
                      <div className="whitespace-pre-wrap">{post.ai_markdown}</div>
                    ) : (
                      <p className="text-muted-foreground italic">No content yet</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {post.content_html && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">HTML (Cached)</h4>
                        <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm whitespace-pre-wrap font-mono">
                          {post.content_html}
                        </pre>
                      </div>
                    )}
                    {post.ai_markdown && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">AI Markdown</h4>
                        <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm whitespace-pre-wrap">
                          {post.ai_markdown}
                        </pre>
                      </div>
                    )}
                    {post.content && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">HTML Content</h4>
                        <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm whitespace-pre-wrap font-mono">
                          {post.content}
                        </pre>
                      </div>
                    )}
                    {!post.ai_markdown && !post.content && !post.content_html && (
                      <p className="text-muted-foreground italic">No content yet</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={statusColors[post.status]}>
                    {statusLabels[post.status]}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Visibility</span>
                  <Badge variant={post.visibility === 'auth_only' ? 'secondary' : 'default'}>
                    {visibilityLabels[post.visibility]}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Author</div>
                    <div className="text-sm text-muted-foreground">{post.author.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Published</div>
                    <div className="text-sm text-muted-foreground">
                      {post.published_at
                        ? new Date(post.published_at).toLocaleString()
                        : 'Not published'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Created</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(post.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Updated</div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(post.updated_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="h-4 w-4" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                {post.categories.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No categories</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {post.categories.map((category) => (
                      <Badge key={category.id} variant="outline">
                        {category.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent>
                {post.tags.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tags</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <Badge key={tag.id} variant="secondary">
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {post.summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{post.summary}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </BlogLayout>
  );
};

export default PostShow;
